<template>
  <div class="review">
    <div class="seo-section">
      <h3>SEO</h3>
      <div class="containers">
        <div class="input-container" v-if="task.content.siteIds[0] === 'casinobernie'">
          <label for="seo-title">SEO Title ({{ characterCount(task.content.htmlTitle) }} characters)</label>
          <input :disabled="!isActive" maxlength="65" class="input" autocomplete="off" id="seo-title" type="text" v-model="task.content.htmlTitle" />
        </div>
        <div class="input-container input-container--full-width">
          <label for="seo-content">SEO Meta Description ({{ characterCount(task.content.htmlMetaDescription) }} characters)</label>
          <input :disabled="!isActive" maxlength="154" class="input" id="seo-content" v-model="task.content.htmlMetaDescription" />
        </div>
      </div>
      <div class="input-container">
        <label for="keyword">Keyword</label>
        <input :disabled="!isActive" class="input" id="keyword" v-model="task.content.keyword" />
      </div>
    </div>

    <div class="content-section">
      <h3>Content</h3>
      <div class="input-container">
        <label for="title">Title ({{ wordCount(task.content.title) }} words)</label>
        <input :disabled="!isActive" class="input" autocomplete="off" id="title" type="text" v-model="task.content.title" />
      </div>
      <div class="textarea-container">
        <label for="content" data-cy="content-words">Content ({{ wordCount(task.content.content) }} words)</label>
        <textarea :disabled="!isActive" class="textarea" id="content" rows="20" v-model="task.content.content"></textarea>
      </div>
    </div>

    <template v-if="task.content.siteIds[0] !== 'casinobernie'">
      <div class="pros-section">
        <div v-for="(pro, index) in task.content.pros" v-bind:key="`pros${index}`">
          <h3>Pros</h3>
          <div class="input-container">
            <label for="title">{{ index + 1 }}</label>
            <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.pros[index]" :data-cy="`pro-${index + 1}`" />
          </div>
        </div>
      </div>
      <div class="cons-section" v-if="task.content.siteIds[0] !== 'casinobernie'">
        <div v-for="(con, index) in task.content.cons" v-bind:key="`cons${index}`">
          <h3>Cons</h3>
          <div class="input-container">
            <label for="title">{{ index + 1 }}</label>
            <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.cons[index]" :data-cy="`con-${index + 1}`" />
          </div>
        </div>
      </div>
    </template>

    <div class="faq-section" v-if="task.content.faq">
      <h3>FAQ</h3>
      <div class="input-container input-container--full-width">
        <label>Title</label>
        <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.faq.title" data-cy="faq-title" />
      </div>
      <div>
        <div class="question" v-for="(qa, index) in task.content.faq.qas" v-bind:key="`question${index}`">
          <div class="input-container input-container--full-width" v-if="qa">
            <label>Question {{ index + 1 }}</label>
            <input :disabled="!isActive" class="input" autocomplete="off" v-model="qa.question" :data-cy="`question-${index + 1}`" />
          </div>
          <div class="input-container input-container--full-width" v-if="qa">
            <label>Answer {{ index + 1 }}</label>
            <input :disabled="!isActive" class="input" autocomplete="off" v-model="qa.answer" :data-cy="`answer-${index + 1}`" />
          </div>
          <div v-if="isActive">
            <div class="circle circle--open" v-on:click="newQuestion()">
              <div class="circle__before"></div>
              <div class="circle__after"></div>
            </div>
            <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteQuestion(index)">
              <div class="circle__before"></div>
              <div class="circle__after"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="how-to-section" v-if="task.content.howTo">
      <h3>How To</h3>
      <div class="input-container input-container--full-width">
        <label>Headline</label>
        <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.howTo.headline" data-cy="howto-headline" />
      </div>
      <div class="textarea-container">
        <label>Description</label>
        <textarea :disabled="!isActive" class="textarea" rows="6" v-model="task.content.howTo.description" data-cy="howto-description"></textarea>
      </div>
      <div class="input-container">
        <label>Time</label>
        <vue-timepicker hide-clear-button v-model="task.content.howTo.time"></vue-timepicker>
      </div>
      <div class="tool" v-for="(tool, index) in task.content.howTo.tools" v-bind:key="`tool-${index}`">
        <div class="input-container">
          <label>Tool {{ index + 1 }}</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.howTo.tools[index]" :data-cy="`tool-${index}`" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newTool()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteTool(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
      <div class="supply" v-for="(supply, index) in task.content.howTo.supplies" v-bind:key="`supply-${index}`">
        <div class="input-container">
          <label>Supply {{ index + 1 }}</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.howTo.supplies[index]" :data-cy="`supply-${index}`" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newSupply()" data-cy="new-supply-button">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteSupply(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
      <div class="step" v-for="(step, index) in task.content.howTo.steps" v-bind:key="`step-${index}`">
        <span>Step {{ index + 1 }}</span>
        <div class="input-container">
          <label>Headline</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="step.headline" :data-cy="`step-headline-${index}`" />
        </div>
        <!-- <div class="input-container">
          <label>Name</label>
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="step.name" />
        </div> -->
        <div class="textarea-container">
          <label>Description</label>
          <textarea :disabled="!isActive" class="textarea" rows="6" v-model="step.description" :data-cy="`step-description-${index}`"></textarea>
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newStep()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteStep(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="pros-cons-section" v-if="task.content.prosConsShortcode">
      <h3>Pros and cons shortcode</h3>
      <div class="input-container input-container--full-width">
        <label>Headline</label>
        <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.prosConsShortcode.headline" data-cy="short-headline" />
      </div>
      <div class="textarea-container">
        <label>Description</label>
        <textarea :disabled="!isActive" class="textarea" rows="6" v-model="task.content.prosConsShortcode.description" data-cy="short-description"></textarea>
      </div>
      <div class="pros" v-for="(pro, index) in task.content.prosConsShortcode.pros" v-bind:key="`pro-${index}`">
        <span>Pro {{ index + 1 }}</span>
        <div class="input-container input-container--full-width">
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.prosConsShortcode.pros[index]" :data-cy="`pro-short-${index}`" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newPro()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deletePro(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
      <div class="cons" v-for="(con, index) in task.content.prosConsShortcode.cons" v-bind:key="`con-${index}`">
        <span>Con {{ index + 1 }}</span>
        <div class="input-container input-container--full-width">
          <input :disabled="!isActive" class="input" autocomplete="off" v-model="task.content.prosConsShortcode.cons[index]" :data-cy="`con-short-${index}`" />
        </div>
        <div v-if="isActive">
          <div class="circle circle--open" v-on:click="newCon()">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
          <div v-if="index !== 0" class="circle circle--close" v-on:click="deleteCon(index)">
            <div class="circle__before"></div>
            <div class="circle__after"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import sharedWordCountScript from '@/assets/shared-scripts/wordcount'
import VueTimepicker from 'vue2-timepicker'
import 'vue2-timepicker/dist/VueTimepicker.css'

export default {
  created() {
    this.wordCount = sharedWordCountScript.wordCounter
  },
  props: {
    task: Object,
  },
  methods: {
    newQuestion() {
      const newQuestion = { question: '', answer: '' }
      this.task.content.faq.qas.push(newQuestion)
    },
    deleteQuestion(index) {
      this.task.content.faq.qas.splice(index, 1)
    },
    newTool() {
      this.task.content.howTo.tools.push('')
    },
    deleteTool(index) {
      this.task.content.howTo.tools.splice(index, 1)
    },
    newSupply() {
      this.task.content.howTo.supplies.push('')
    },
    deleteSupply(index) {
      this.task.content.howTo.supplies.splice(index, 1)
    },
    newStep() {
      const newStep = { headline: '', name: '', description: '' }
      this.task.content.howTo.steps.push(newStep)
    },
    deleteStep(index) {
      this.task.content.howTo.steps.splice(index, 1)
    },
    newPro() {
      this.task.content.prosConsShortcode.pros.push('')
    },
    deletePro(index) {
      this.task.content.prosConsShortcode.pros.splice(index, 1)
    },
    newCon() {
      this.task.content.prosConsShortcode.cons.push('')
    },
    deleteCon(index) {
      this.task.content.prosConsShortcode.cons.splice(index, 1)
    },
    characterCount(string) {
      return string.length
    },
  },
  components: {
    VueTimepicker,
  },
  computed: {
    ...mapGetters(['user']),
    isActive() {
      const isAdmin = this.user.data.firestore.isAdmin
      const status = this.task.status

      if (isAdmin) {
        return status != 'available' ? false : true
      } else {
        return status == 'available' || status == 'claimed' || status == 'revision-needed' ? true : false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/shared-styles/checkbox.scss';
@import '@/assets/shared-styles/input.scss';
@import '@/assets/shared-styles/sections.scss';
@import '@/assets/shared-styles/select.scss';
@import '@/assets/shared-styles/textarea.scss';

.content {
  background: white;
}

.circle {
  position: absolute;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: var(--color-cta);
  cursor: pointer;
  z-index: 2;
}
.circle--open {
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
}
.circle__before,
.circle__after {
  content: '';
  position: absolute;
  width: 16px;
  height: 1px;
  background-color: white;
  border-radius: 0;
  top: 13px;
  z-index: 0;
}
.circle--close > .circle__before,
.circle--close > .circle__after {
  background-color: #43425d;
}
.circle--close:hover > .circle__before,
.circle--close:hover > .circle__after {
  height: 2px;
  background-color: var(--color-cta);
}
.circle__before {
  left: 5px;
}
.circle__after {
  -webkit-transform: rotate(-90deg);
  -moz-transform: rotate(-90deg);
  transform: rotate(-90deg);
  right: 5px;
}
.circle--close {
  background-color: transparent;
  right: 0;
  top: 0;
  transform: translate(-50%, 50%);
}
.circle--close .circle__before {
  transform: rotate(225deg);
}
.circle--close .circle__after {
  transform: rotate(-225deg);
}
.circle--centered {
  top: 5px;
}
</style>
